import styled from "styled-components";
import {
  Accordion as ChakraAccordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@chakra-ui/react";

import { useEffect, useState } from "react";
import { useTheme } from "src/apps/providers/ThemeStore";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { DefaultOrdersSectionHeader } from "./DefaultOrdersSectionHeader";
import { ChevronIcon } from "src/apps/components/chevronIcon/ChevronIcon";
import { colors } from "@permisso/permisso-components";

interface OrdersSectionProps {
  content: JSX.Element;
  title?: string;
  description?: string;
  header?: JSX.Element;
  subHeader?: JSX.Element;
  isCompleted?: boolean;
  collapseOnComplete?: boolean;
  disabled?: boolean;
}

export const OrdersSection = ({
  content,
  title,
  description,
  header,
  subHeader,
  isCompleted = false,
  disabled = false,
  collapseOnComplete = false,
}: OrdersSectionProps) => {
  const { isMobile } = useIsMedia();
  const { successColor } = useTheme();

  const [isToggled, setIsToggled] = useState(true);
  const handleToggle = () => setIsToggled((prev) => !prev);

  useEffect(() => {
    if (isCompleted && collapseOnComplete) {
      setIsToggled(false);
    }
  }, [isCompleted, collapseOnComplete]);

  const borderColor = isCompleted === true ? successColor : colors.grey400;
  const borderWidth = isCompleted === true ? "2px" : "1px";
  const defaultHeader =
    title && description ? (
      <DefaultOrdersSectionHeader
        title={title}
        description={description}
        status={isCompleted ? "completed" : "in_progress"}
      />
    ) : null;

  if (isMobile && !header && defaultHeader) {
    return (
      <MobileContainer>
        {defaultHeader}
        {content}
      </MobileContainer>
    );
  }

  return (
    <StyledChakraAccordion
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      allowMultiple
      index={isToggled ? [0] : []}
    >
      <StyledAccordionItem isDisabled={disabled}>
        <StyledAccordionButton onClick={handleToggle} $isOpen={isToggled}>
          <HeaderContainer>{header ?? defaultHeader}</HeaderContainer>
          {!disabled ? <ChevronIcon rotated={isToggled} /> : null}
        </StyledAccordionButton>
        <StyledAccordionPanel>
          {isToggled && subHeader}
          {content}
        </StyledAccordionPanel>
      </StyledAccordionItem>
    </StyledChakraAccordion>
  );
};

const StyledChakraAccordion = styled(ChakraAccordion)<{
  $borderColor: string;
  $borderWidth: string;
}>`
  border: ${({ $borderWidth, $borderColor }) => `${$borderWidth} solid ${$borderColor}`};
  border-radius: 1.25rem;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  padding: 0 !important; // To override default chakra padding
`;

const StyledAccordionButton = styled(AccordionButton)<{ $isOpen: boolean }>`
  border-bottom: none;
  padding-inline-start: 0 !important; // To override default chakra padding
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-inline-end: 1.75rem !important;
  border-radius: ${({ $isOpen }) => ($isOpen ? "1.25rem 1.25rem 0 0" : "1.25rem")} !important;
  border-bottom: ${({ $isOpen }) => ($isOpen ? `1px solid ${colors.grey400}` : "none")} !important;
  &[disabled],
  &[aria-disabled="true"] {
    background: inherit !important;
    color: inherit !important;
    opacity: 1 !important;
    cursor: not-allowed;
  }
`;

const StyledAccordionItem = styled(AccordionItem)`
  border-top: none;
  border-bottom: none;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
  text-align: left;
`;

const MobileContainer = styled.div`
  padding: 0rem 1rem 1.75rem 1rem;
  border-bottom: 0.0625rem solid ${colors.grey400};
`;
